// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — SourceSigil  (THE ONE TRUE PROVENANCE MARK)
// ────────────────────────────────────────────────────────────────────
// Every card you own remembers WHERE it came from. A Beb pulled from the
// Shadow Fox pack is rarer-feeling than the same Beb bought at the Vault,
// because it literally wears the Shadow Fox mark. This is provenance as a
// first-class, visible thing — Pokémon set symbols, but bespoke per source
// and animated by a radiance tier (earned sources glow flashier; shop /
// vault sources stay clean and restrained).
//
// ── WHY THIS EXISTS ──────────────────────────────────────────────────
// The collection already tracks WHAT FOIL a card is (`holoTiers`). It did
// NOT track WHERE the card came from. This registry is the source-side
// twin of HoloFoil: one recipe table, one shared component, drops in
// anywhere a card is inspected. Add a new source = one entry here, and it
// propagates to every surface at once. No drift.
//
// ── THE DESIGN CONTRACT (caP'n's law) ────────────────────────────────
// "Every holo we work on has to be better than the last." Same here: each
// sigil is its OWN mark, never a recolor of another. A source's radiance
// level decides how loud it announces itself — `earned` provenance flares;
// `vault` / `shop` provenance reads as a clean stamp.
//
// ── RADIANCE LEVELS ──────────────────────────────────────────────────
//   0  flat      — a clean static stamp (shop / vault). No animation.
//   1  glimmer   — a slow breathing glow ring.
//   2  shimmer   — breathing glow + a slow rotating halo behind the glyph.
//   3  radiant   — the full earned treatment: pulsing glow, rotating halo,
//                  AND a sweeping specular glint across the glyph. Reserved
//                  for the prestige / "earned the hard way" sources.
//
// ── USAGE ────────────────────────────────────────────────────────────
//   // a single mark (in a picker tile, or top-of-card on a viewer):
//   <SourceSigil source="booster_shadowfox" size={44} />
//
//   // dim a locked/preview mark:
//   <SourceSigil source="vault" size={28} intensity={0.6} />
//
// Pointer-events are off — it never eats clicks. Render it wherever; it is
// self-contained (its own positioned box), so it does NOT need a
// position:relative parent the way HoloFoil does.
// ════════════════════════════════════════════════════════════════════

import React from "react";

// ── SOURCE_SIGILS — the recipe table (single source of provenance truth)
// Each source id maps to a recipe. Fields:
//   label     : human-facing provenance line ("From the Shadow Fox Pack").
//   glyph     : an SVG <path>/<g> string drawn inside a 0 0 100 100 viewBox,
//               centered, ~70 units tall. Stroke/fill use `color`.
//   color     : the sigil's accent color (glyph + glow + halo).
//   color2    : optional secondary accent (used by the halo gradient).
//   radiance  : 0..3 — how loud the mark announces itself (see header).
//   fill      : true → glyph is filled; false → stroked outline (default).
//
// EVERY source now has its proper sigil ("every source" pass, Jul 2026):
// 4 foundation sources + 29 pack marks + limited + emporium. Adding a new
// pack = one new `booster_*` entry here + one PACK_SOURCE line in
// progression.js; that's the entire footprint.
// ── MINERIUM1_GRIDDLE — one seal, authored once, drawn twice ─────────
// `hot` is the Mythic state: rim closed in light (the Fable rim is
// broken, because a Fable run is the Minerium walked but not emptied),
// shreds filled rather than outlined, and the Jewel solid.
//
// Drawn in the standard 0 0 100 100 box, ~70 units tall, centred. The
// shred strokes are deliberately SHORT and thin — caP'n asked for mini
// hash browns "without being intrusive", and a sigil that reads as
// breakfast at 28px would be the intrusive version.
const MINERIUM1_GRIDDLE = (c, hot) => `
  <g stroke="${c}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
    <!-- the plate: a griddle is round. Broken rim when cold, closed when hot. -->
    ${hot
      ? `<circle cx="50" cy="50" r="34"/>`
      : `<path d="M50 16 A34 34 0 1 1 49.9 16" stroke-dasharray="26 7"/>`}
    <!-- the sear, in TWO segments with the Jewel in the gap. Drawn as
         one unbroken diagonal it read as a prohibition sign and buried
         the Jewel underneath it; broken, the plate stays a plate and
         the Jewel is the thing at the centre of it. -->
    <path d="M23 69 L38 58" stroke-width="6"/>
    <path d="M62 42 L77 31" stroke-width="6"/>
    <!-- the shreds: four mini hash browns along the sear, off-centre so
         they never crowd the Jewel. Four, not six -- at 28px six
         became a smear. -->
    <g stroke-width="${hot ? 4 : 3.4}" opacity="${hot ? 1 : 0.85}">
      <path d="M30 76 L38 70"/>
      <path d="M20 58 L28 52"/>
      <path d="M72 48 L80 42"/>
      <path d="M62 30 L70 24"/>
    </g>
    <!-- the Crimson Jewel: his seat, where a rank pin sits -->
    <path d="M50 38 L61 50 L50 62 L39 50 Z"
      ${hot ? `fill="${c}" stroke-width="3"` : `stroke-width="4.5"`}/>
  </g>`;

// ── THE_GAUNTLET — the mode's own mark, authored once, drawn twice ──
// caP'n, 2026-08-22: *"The Gauntlet sigil — yesss :3"*, and on the
// design: *"confidence V'ael, let's not make anything too similar,
// that is how slop starts."*
//
// So it is not the Griddle in grey. The Griddle is a PLATE with a
// stroke across it; this is a FIST with four plates closing on it, and
// the four plates are the same gesture `holo_gauntlet`'s foil makes —
// the mark and the foil are one idea at two scales.
//
// `hot` is the Mythic state: the four plates have arrived and touch
// the fist, the knuckle bands fill, and the cuff closes.
const THE_GAUNTLET = (c, hot) => `
  <g stroke="${c}" stroke-width="4.6" stroke-linecap="round" stroke-linejoin="round" fill="none">
    <!-- the four plates, closing. Short of the fist when cold, on it when hot. -->
    <g stroke-width="${hot ? 5.4 : 4.4}" opacity="${hot ? 1 : 0.78}">
      <path d="M50 ${hot ? 24 : 15} L43 ${hot ? 15 : 6} M50 ${hot ? 24 : 15} L57 ${hot ? 15 : 6}"/>
      <path d="M50 ${hot ? 79 : 88} L43 ${hot ? 88 : 97} M50 ${hot ? 79 : 88} L57 ${hot ? 88 : 97}"/>
      <path d="M${hot ? 22 : 13} 52 L${hot ? 13 : 4} 45 M${hot ? 22 : 13} 52 L${hot ? 13 : 4} 59"/>
      <path d="M${hot ? 78 : 87} 52 L${hot ? 87 : 96} 45 M${hot ? 78 : 87} 52 L${hot ? 87 : 96} 59"/>
    </g>
    <!-- THE KNUCKLES. Four folded fingers, seen end-on. This is the one
         shape that says "gauntlet" and not "box" — the first draft put
         three horizontal bands on a rounded rect and it read as a
         document icon at every size. -->
    <g ${hot ? `fill="${c}"` : ""} stroke-width="3.6">
      <path d="M33 40 Q33 33 38 33 Q43 33 43 40"/>
      <path d="M43 39 Q43 31 48.5 31 Q54 31 54 39"/>
      <path d="M54 39 Q54 31 59.5 31 Q65 31 65 39"/>
      <path d="M65 41 Q65 34 69 34 Q73 34 73 41"/>
    </g>
    <!-- the back of the hand, and the finger seams down it -->
    <path d="M33 40 L73 41 L71 62 Q70 68 63 68 L41 68 Q34 68 34 61 Z"
      ${hot ? `fill="${c}" fill-opacity="0.18"` : ""}/>
    <g stroke-width="2.6" opacity="0.9">
      <path d="M43 42 L43 56"/>
      <path d="M54 42 L54 56"/>
      <path d="M65 43 L65 56"/>
    </g>
    <!-- the thumb, folded across the front. Thick, so it survives 28px. -->
    <path d="M33 48 Q23 53 27 61 Q31 68 41 68" stroke-width="${hot ? 5.6 : 4.8}"/>
    <!-- the cuff. Broken when cold, closed when hot. -->
    ${hot
      ? `<path d="M35 74 L69 74" stroke-width="5.4"/>`
      : `<path d="M35 74 L45 74 M59 74 L69 74" stroke-width="4.6"/>`}
  </g>`;

export const SOURCE_SIGILS = {
  // ── STARTER — the very first cards you're handed. Every player has these,
  // so this is the sigil you'll see first. A budding sigil: a seed/sprout
  // inside a ring — "where it all began." Gentle radiance; it's humble, but
  // it's YOURS from day one. ───────────────────────────────────────────
  starter: {
    label: "From your Starter Deck",
    color: "#6ee7a8",
    color2: "#b8f5d4",
    radiance: 1,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="50" r="33"/>
        <path d="M50 66 L50 40"/>
        <path d="M50 46 Q38 44 34 32 Q47 32 50 44"/>
        <path d="M50 50 Q62 46 67 34 Q53 35 50 48"/>
        <path d="M42 70 Q50 64 58 70" />
      </g>`,
  },

  // ── EARNED — prestige provenance, full radiance. The flashy one. ────
  // A rayed star-burst: light thrown outward. "You earned this."
  earned: {
    label: "Earned in Battle",
    color: "#ffd24a",
    color2: "#fff4c2",
    radiance: 3,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 14 L57 43 L86 50 L57 57 L50 86 L43 57 L14 50 L43 43 Z"/>
        <line x1="50" y1="4"  x2="50" y2="18"/>
        <line x1="50" y1="82" x2="50" y2="96"/>
        <line x1="4"  y1="50" x2="18" y2="50"/>
        <line x1="82" y1="50" x2="96" y2="50"/>
        <circle cx="50" cy="50" r="7" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  // ── TRADE — a card that changed hands between players (2026-09-06).
  // Two arrows passing each other: it came from someone, something went
  // back. Violet, the friends-layer accent, because that is the door it
  // came through. ─────────────────────────────────────────────────────
  trade: {
    label: "Traded from a Player",
    color: "#c084fc",
    color2: "#e9d5ff",
    radiance: 1,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M22 38 L66 38 M56 28 L66 38 L56 48"/>
        <path d="M78 62 L34 62 M44 52 L34 62 L44 72"/>
      </g>`,
  },

  // ── VAULT — the single-card storefront. Clean, restrained stamp. ────
  // A keyhole inside a diamond: precise, deliberate, "hand-picked."
  vault: {
    label: "From the Foil Vault",
    color: "#7fd8ff",
    color2: "#bfeeff",
    radiance: 1,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 12 L84 50 L50 88 L16 50 Z"/>
        <circle cx="50" cy="44" r="9"/>
        <path d="M50 53 L45 70 L55 70 Z" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  // ── BOOSTER — generic pack pull (any pack without its own sigil yet).
  // A torn-open envelope / pack flap. The default booster mark; specific
  // packs override it with their own named sigil below.
  booster: {
    label: "From a Booster Pack",
    color: "#c8963c",
    color2: "#e8b84b",
    radiance: 1,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <rect x="18" y="26" width="64" height="52" rx="4"/>
        <path d="M18 30 L50 56 L82 30"/>
        <path d="M50 8 L58 22 L42 22 Z" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  // ══════════════════════════════════════════════════════════════════
  // ── CORE — THE SEALED CORE. ONE MARK FOR THE WHOLE CORE. ──────────
  // ══════════════════════════════════════════════════════════════════
  // ⭐ caP'n, 2026-08-22: *"the Class, Tomes, and Support [shelves] will
  // be bundled into a tab you must first click on called 'Core' ... that
  // way they will always guaranteed be cores even when I make new cards
  // and we make new booster packs, they will still be 'Core' you see? so
  // for all cards in that section they must have the unique 'Core'
  // sigil, only one, no holo variations etc — just the one single Core
  // sigil for every pack that exists or will exist in Class, Tomes,
  // Support (and if we add more, those too)."*
  //
  // ── WHY THIS IS ONE MARK AND NOT THIRTEEN ─────────────────────────
  // Thirteen bespoke pack marks were authored below and they are all
  // still here, unwired (see THE SHELVED SIGILS banner). They were not
  // wrong — they were the wrong ANSWER TO THIS QUESTION. A provenance
  // mark answers "where is this card from"; for a core pack the honest
  // answer is not "the Phantom Mastery packet", it is THE CORE — the
  // permanent, always-buyable spine of the game that every future class,
  // school and support drawer joins. A mark that changes every time a
  // drawer is added was never saying that. This one is, and it is the
  // ONE thing in this registry that is deliberately shared.
  //
  // ── THE DRAWING ───────────────────────────────────────────────────
  // A closed ring — the core is sealed, it does not open and it does not
  // move. Four short ticks at the compass points — the drawers that hang
  // off it (School, Tomes, Support, and the fourth that does not exist
  // yet; the mark already has room for it). A small ring at the centre
  // holding a solid diamond — the thing itself, present and filled.
  // It reads as a set symbol at 14px on a shelf chip, which is the size
  // it will be seen at most often.
  //
  // ⚠ RADIANCE 0, AND THAT IS THE POINT. `flat` is a clean static stamp
  // with no animation. The core is the thing you can always buy; it does
  // not get to flare like an earned mark. Never raise this — if the Core
  // announces itself as loudly as a prestige source, the prestige
  // sources stop meaning anything, which is the same ruling that took
  // the Foil and Activity shelves down.
  core: {
    label: "From the Core",
    color: "#d4a84b",
    color2: "#f2d894",
    radiance: 0,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <!-- the seal: closed, unbroken. -->
        <circle cx="50" cy="50" r="34"/>
        <!-- the drawers: four ticks, outside the seal, at the compass points. -->
        <g stroke-width="4.5">
          <path d="M50 8  L50 16"/>
          <path d="M50 84 L50 92"/>
          <path d="M8  50 L16 50"/>
          <path d="M84 50 L92 50"/>
        </g>
        <!-- the core itself: held in a small ring, and solid. -->
        <circle cx="50" cy="50" r="17" stroke-width="4"/>
        <path d="M50 39 L61 50 L50 61 L39 50 Z" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  // ══════════════════════════════════════════════════════════════════
  // ── THE SHELVED SIGILS — AUTHORED, KEPT, NOT CURRENTLY WORN ───────
  // ══════════════════════════════════════════════════════════════════
  // ⭐ caP'n, 2026-08-22: *"shelve the sigils you created for all those
  // individual packs because we will relocate them to different areas
  // and ways to obtain those sigils later."*
  //
  // Every `booster_*` recipe from here down is INTACT and UNCHANGED. The
  // shop simply stopped asking for them: PACK_SOURCE in progression.js
  // now points all thirteen core packs at `core`, so that is the mark
  // the pull stamps, the shelf chip shows and the detail badge draws.
  //
  // ⚠ DO NOT DELETE THEM, AND DO NOT PRUNE THE ONES THAT LOOK ORPHANED.
  // Two separate reasons, and each alone is sufficient:
  //   1. Cards already in players' collections carry these source ids in
  //      Firebase. Deleting a recipe does not un-stamp a card; it makes
  //      an owned card fall back to the generic `booster` envelope and
  //      lose provenance it was legitimately given.
  //   2. They are being RELOCATED, not retired. When the Space
  //      Ascendants mark becomes something you earn somewhere rather
  //      than something you buy, the recipe is already written and the
  //      relocation is one PACK_SOURCE-shaped line elsewhere.
  //
  // Sigils below that were never core-shelf marks (foil editions, the
  // Minerium, the Gauntlet, limited, emporium, vault, starter, earned)
  // are untouched by any of this and are still wired exactly as before.

  // ── SHADOW FOX PACK — the first named pack sigil. PoC proof. ────────
  // A sly fox mask / ears + eyes motif in the Shadow tier's violet. A card
  // wearing this is provably "the one from the Shadow Fox pack." rarer <3
  booster_shadowfox: {
    label: "From the Shadow Fox Pack",
    color: "#9d6bff",
    color2: "#c9aaff",
    radiance: 2,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M22 30 L34 14 L42 34"/>
        <path d="M78 30 L66 14 L58 34"/>
        <path d="M24 36 Q50 24 76 36 Q72 64 50 82 Q28 64 24 36 Z"/>
        <circle cx="40" cy="50" r="3.5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="60" cy="50" r="3.5" fill="CURRENTCOLOR" stroke="none"/>
        <path d="M50 60 L46 66 L54 66 Z" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  // ════════════════════════════════════════════════════════════════════
  // EVERY PACK, ITS PROPER SIGIL (Jul 2026 — "every source" pass).
  // 29 packs, 29 marks. Each glyph is its OWN silhouette echoing its
  // pack's soul — never a recolor, never a shared shape. Radiance law:
  // core packs = 1 (clean everyday stamps), holo Editions & Premier = 2
  // (guaranteed-foil provenance announces itself), Chaos = 2.
  // Colors follow each pack's accentColor so wrapper and mark rhyme.
  // ════════════════════════════════════════════════════════════════════

  // ── CORE SEVEN ──────────────────────────────────────────────────────
  // VOID ECLIPSE — the occluded sun: a crescent of stolen light, corona
  // ticks marking where the light escapes.
  booster_void_eclipse: {
    label: "From the Void Eclipse Pack",
    color: "#7c3aed", color2: "#b79aff", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 18 A32 32 0 1 0 50 82 A24 24 0 1 1 50 18 Z" fill="CURRENTCOLOR" stroke="none" opacity="0.9"/>
        <line x1="50" y1="6"  x2="50" y2="14"/>
        <line x1="81" y1="26" x2="75" y2="31"/>
        <line x1="81" y1="74" x2="75" y2="69"/>
        <line x1="90" y1="50" x2="82" y2="50"/>
      </g>`,
  },
  // SOLAR ASCENSION — the sun climbing the horizon, rays reaching up.
  booster_solar_ascension: {
    label: "From the Solar Ascension Pack",
    color: "#3b82f6", color2: "#93c5fd", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M28 62 A22 22 0 0 1 72 62"/>
        <line x1="14" y1="62" x2="86" y2="62"/>
        <line x1="50" y1="18" x2="50" y2="28"/>
        <line x1="28" y1="30" x2="34" y2="38"/>
        <line x1="72" y1="30" x2="66" y2="38"/>
        <path d="M42 82 L50 72 L58 82"/>
      </g>`,
  },
  // CLOCKWORK DOMINION — the governing gear, hands set to the hour of rule.
  booster_clockwork_dominion: {
    label: "From the Clockwork Dominion Pack",
    color: "#0d9488", color2: "#5eead4", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="50" r="24"/>
        <line x1="50" y1="14" x2="50" y2="22"/>
        <line x1="50" y1="78" x2="50" y2="86"/>
        <line x1="14" y1="50" x2="22" y2="50"/>
        <line x1="78" y1="50" x2="86" y2="50"/>
        <line x1="25" y1="25" x2="31" y2="31"/>
        <line x1="75" y1="25" x2="69" y2="31"/>
        <line x1="25" y1="75" x2="31" y2="69"/>
        <line x1="75" y1="75" x2="69" y2="69"/>
        <path d="M50 50 L50 34"/>
        <path d="M50 50 L62 56"/>
        <circle cx="50" cy="50" r="3.5" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // DRAGON'S WRATH — three claw rends and the fang between them.
  booster_dragons_wrath: {
    label: "From the Dragon's Wrath Pack",
    color: "#dc2626", color2: "#fca5a5", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M26 18 Q38 44 30 78"/>
        <path d="M50 14 Q58 46 48 84"/>
        <path d="M74 18 Q84 46 70 78"/>
        <path d="M46 30 L52 42 L58 30" stroke-width="4"/>
      </g>`,
  },
  // SUPREME COUNCIL — the hex seal, three pillars standing within.
  booster_supreme_council: {
    label: "From the Supreme Council Pack",
    color: "#d4a84b", color2: "#f0d9a0", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 12 L82 31 L82 69 L50 88 L18 69 L18 31 Z"/>
        <line x1="38" y1="40" x2="38" y2="64"/>
        <line x1="50" y1="36" x2="50" y2="64"/>
        <line x1="62" y1="40" x2="62" y2="64"/>
        <line x1="32" y1="64" x2="68" y2="64"/>
        <circle cx="50" cy="26" r="3" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // INSTINCT RISING — three claws spiraling up around the rising chevron.
  booster_instinct_rising: {
    label: "From the Instinct Rising Pack",
    color: "#16a34a", color2: "#86efac", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 46 Q34 40 30 22 Q46 26 50 42"/>
        <path d="M54 50 Q70 48 80 34 Q66 32 54 44"/>
        <path d="M48 54 Q36 64 36 80 Q50 72 52 58"/>
        <path d="M42 32 L50 20 L58 32"/>
      </g>`,
  },
  // UNSTABLE WORLD — the cracked planet, fragments already drifting.
  booster_unstable_world: {
    label: "From the Unstable World Pack",
    color: "#ff2266", color2: "#ff9ab8", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="52" r="28"/>
        <path d="M34 30 L44 44 L38 54 L50 66 L46 80"/>
        <path d="M44 44 L58 40"/>
        <path d="M74 22 L80 14" stroke-width="4"/>
        <circle cx="84" cy="26" r="3" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="20" cy="20" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  // ── HOLO EDITIONS — guaranteed-foil provenance, radiance 2. Each is
  // its core pack's soul EXALTED into a new composition (never a recolor;
  // Shadow Edition wears the fox above). ─────────────────────────────
  // VE MYTHIC — the eclipse caught inside a faceted prism.
  booster_void_eclipse_mythic: {
    label: "From Void Eclipse — Mythic Edition",
    color: "#ff66cc", color2: "#ffb8e6", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 8 L88 50 L50 92 L12 50 Z"/>
        <path d="M50 32 A18 18 0 1 0 50 68 A13 13 0 1 1 50 32 Z" fill="CURRENTCOLOR" stroke="none" opacity="0.9"/>
        <line x1="50" y1="8"  x2="50" y2="22" stroke-width="3"/>
        <line x1="12" y1="50" x2="26" y2="50" stroke-width="3"/>
        <line x1="88" y1="50" x2="74" y2="50" stroke-width="3"/>
      </g>`,
  },
  // SA GOLD — the sun crowned, a molten drop falling from it.
  booster_solar_ascension_gold: {
    label: "From Solar Ascension — Gold Edition",
    color: "#ffd700", color2: "#fff3c4", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="48" r="20"/>
        <path d="M32 26 L38 14 L46 24 L50 10 L54 24 L62 14 L68 26"/>
        <path d="M50 72 Q44 82 50 88 Q56 82 50 72 Z" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // CD DEPTHS — the gear drowned beneath the waveline, bubbles escaping.
  booster_clockwork_dominion_depths: {
    label: "From Clockwork Dominion — Depths Edition",
    color: "#00aaff", color2: "#8fdcff", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M12 34 Q24 26 36 34 Q48 42 60 34 Q72 26 88 34"/>
        <circle cx="50" cy="62" r="18"/>
        <line x1="50" y1="36" x2="50" y2="44"/>
        <line x1="50" y1="80" x2="50" y2="88"/>
        <line x1="24" y1="62" x2="32" y2="62"/>
        <line x1="68" y1="62" x2="76" y2="62"/>
        <circle cx="68" cy="22" r="3"/>
        <circle cx="76" cy="12" r="2"/>
      </g>`,
  },
  // DW SOLREND — the sun itself torn open by the claw.
  booster_dragons_wrath_solrend: {
    label: "From Dragon's Wrath — Solrend Edition",
    color: "#ff2266", color2: "#ffb0c4", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="50" r="28"/>
        <path d="M24 76 Q46 52 76 24" stroke-width="7"/>
        <path d="M38 62 Q50 52 62 38" stroke-width="3" opacity="0.7"/>
        <circle cx="70" cy="68" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="30" cy="32" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // SC A'ETERNI — the seal beneath the eternal halo rings.
  booster_supreme_council_aeterni: {
    label: "From Supreme Council — A'eterni Edition",
    color: "#f0f0ff", color2: "#ffffff", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 30 L74 44 L74 72 L50 86 L26 72 L26 44 Z"/>
        <path d="M28 22 A26 10 0 0 1 72 22" stroke-width="4"/>
        <path d="M34 12 A20 8 0 0 1 66 12" stroke-width="3" opacity="0.7"/>
        <circle cx="50" cy="58" r="4" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // VE VOID — the maelstrom: the spiral that swallows.
  booster_void_eclipse_void: {
    label: "From Void Eclipse — Void Edition",
    color: "#9333ea", color2: "#c58fff", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 12 A38 38 0 1 1 14 54"/>
        <path d="M50 26 A24 24 0 1 1 27 54" stroke-width="4"/>
        <path d="M50 40 A11 11 0 1 1 39 52" stroke-width="3"/>
        <circle cx="50" cy="51" r="3.5" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // UW J'YNXI — the cracked world whose crack became a grin.
  booster_unstable_world_jynxi: {
    label: "From Unstable World — J'ynxi Edition",
    color: "#2ee66b", color2: "#a4f5c3", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="50" r="30"/>
        <path d="M30 54 Q50 74 70 54" stroke-width="6"/>
        <path d="M36 36 L44 40" stroke-width="4"/>
        <circle cx="62" cy="38" r="3.5" fill="CURRENTCOLOR" stroke="none"/>
        <path d="M76 20 L84 12" stroke-width="4"/>
      </g>`,
  },

  // ── MONO-SCHOOL ──────────────────────────────────────────────────────
  // SPACE ASCENDANTS — a constellation: three stars, one path between.
  booster_space_ascendants: {
    label: "From the Space Ascendants Pack",
    color: "#60a5fa", color2: "#bfdbfe", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M26 70 L50 44 L74 30" stroke-dasharray="3 6"/>
        <path d="M26 62 L29 68 L26 78 L23 68 Z M18 70 L34 70" stroke-width="4"/>
        <path d="M50 34 L54 42 L50 54 L46 42 Z M40 44 L60 44" stroke-width="4"/>
        <path d="M74 20 L77 27 L74 40 L71 27 Z M64 30 L84 30" stroke-width="4"/>
      </g>`,
  },
  // PHANTOM MASTERY — the phantom's eye, one tear it never shed.
  booster_phantom_mastery: {
    label: "From the Phantom Mastery Pack",
    color: "#7c3aed", color2: "#c4b5fd", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M16 48 Q50 22 84 48 Q50 74 16 48 Z"/>
        <circle cx="50" cy="48" r="9"/>
        <circle cx="50" cy="48" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
        <path d="M50 66 Q46 74 50 80 Q54 74 50 66 Z" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // TIME ENGINE — the hourglass held in the engine's arc.
  booster_time_engine: {
    label: "From the Time Engine Pack",
    color: "#14b8a6", color2: "#99f6e4", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M20 32 A34 34 0 0 1 80 32" stroke-dasharray="7 6"/>
        <path d="M36 30 L64 30 L52 52 L64 74 L36 74 L48 52 Z"/>
        <circle cx="50" cy="66" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="50" cy="44" r="2" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // DRAGON'S ARMORY — the scaled shield, forged not grown.
  booster_dragons_armory: {
    label: "From the Dragon's Armory Pack",
    color: "#f97316", color2: "#fdba74", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 12 L80 24 L80 52 Q80 76 50 90 Q20 76 20 52 L20 24 Z"/>
        <path d="M34 36 L50 46 L66 36"/>
        <path d="M34 50 L50 60 L66 50"/>
        <path d="M40 66 L50 72 L60 66"/>
      </g>`,
  },

  // ── TOMES ───────────────────────────────────────────────────────────
  // SHADOW GAMBLER — the tilted die that always knows its roll.
  booster_shadow_gambler: {
    label: "From the Shadow Gambler's Fortune Pack",
    color: "#f59e0b", color2: "#fcd34d", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M22 66 A32 32 0 0 1 40 22" stroke-width="4" stroke-dasharray="5 6"/>
        <rect x="34" y="34" width="42" height="42" rx="7" transform="rotate(12 55 55)"/>
        <circle cx="48" cy="50" r="3.5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="64" cy="62" r="3.5" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // RITUAL'S ASCENT — steps rising within the circle to the flame.
  booster_rituals_ascent: {
    label: "From the Ritual's Ascent Pack",
    color: "#a855f7", color2: "#d8b4fe", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="52" r="32"/>
        <path d="M30 70 L42 70 L42 58 L54 58 L54 46 L66 46"/>
        <path d="M66 40 Q62 32 66 24 Q72 30 70 36 Q69 39 66 40 Z" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // NIHIL'S EMBRACE — two arms cradling the emptiness between them.
  booster_nihils_embrace: {
    label: "From the Nihil's Embrace Pack",
    color: "#8b5cf6", color2: "#c4b5fd", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M38 18 Q14 38 22 66 Q28 82 42 86"/>
        <path d="M62 18 Q86 38 78 66 Q72 82 58 86"/>
        <circle cx="50" cy="52" r="10" stroke-dasharray="4 5"/>
      </g>`,
  },
  // ── THE FOUR SCHOOLS THAT HAD NO PACK (2026-08-22) ──────────────
  // Construction, Treasure, Omnia and Ancient Sorcery. Eight Tome ilks,
  // eight packs, eight marks — a card can now always say which school
  // it came out of, which four of them could not before today.

  // ARCHITECT'S CODEX — the raised foundation under an open sky.
  booster_architects_codex: {
    label: "From The Architect's Codex Pack",
    color: "#34d399", color2: "#a7f3d0", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M20 78 L80 78"/>
        <path d="M30 78 L30 46 L50 30 L70 46 L70 78"/>
        <path d="M42 78 L42 58 L58 58 L58 78"/>
        <circle cx="50" cy="18" r="5"/>
      </g>`,
  },
  // TREASURE HOARD — the hung pendant over crossed steel.
  booster_treasure_hoard: {
    label: "From The Treasure Hoard Pack",
    color: "#eab308", color2: "#fde68a", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M26 30 Q50 14 74 30"/>
        <path d="M50 30 L50 50"/>
        <path d="M50 50 L60 62 L50 74 L40 62 Z"/>
        <path d="M24 76 L38 62"/><path d="M76 76 L62 62"/>
      </g>`,
  },
  // OMNIA'S DOMINION — the whole ring, held closed. Nihil's opposite.
  booster_omnias_dominion: {
    label: "From the Omnia's Dominion Pack",
    color: "#c084fc", color2: "#e9d5ff", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="50" r="32"/>
        <circle cx="50" cy="50" r="16"/>
        <circle cx="50" cy="50" r="5" fill="CURRENTCOLOR" stroke="none"/>
        <path d="M50 18 L50 34"/><path d="M50 66 L50 82"/>
        <path d="M18 50 L34 50"/><path d="M66 50 L82 50"/>
      </g>`,
  },
  // ANCIENT SORCERY — the old crescent over a falling drop.
  booster_ancient_sorcery: {
    label: "From the Ancient Sorcery Pack",
    color: "#ef4444", color2: "#fca5a5", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M64 22 A28 28 0 1 0 64 62 A22 22 0 1 1 64 22 Z"/>
        <path d="M50 62 Q42 74 50 82 Q58 74 50 62 Z" fill="CURRENTCOLOR" stroke="none"/>
        <path d="M22 30 L28 36"/><path d="M78 74 L72 68"/>
      </g>`,
  },
  // CONTRACT BROKER — the scroll, sealed. Read before you sign.
  booster_contract_broker: {
    label: "From the Contract Broker Pack",
    color: "#d97706", color2: "#fbbf24", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M30 18 Q24 18 24 26 L24 74 Q24 82 32 82 L70 82"/>
        <path d="M30 18 L74 18 Q80 18 80 26 L80 70"/>
        <line x1="36" y1="34" x2="66" y2="34" stroke-width="3.5"/>
        <line x1="36" y1="46" x2="66" y2="46" stroke-width="3.5"/>
        <circle cx="66" cy="68" r="9"/>
        <circle cx="66" cy="68" r="3" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  // ── PREMIER — radiance 2. ───────────────────────────────────────────
  // ALL-STARS MYTHIC — the star that carries stars.
  booster_all_stars_mythic: {
    label: "From the All-Stars — Mythic Bundle",
    color: "#ff66cc", color2: "#ffc2e8", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 14 L59 40 L86 40 L64 56 L72 84 L50 66 L28 84 L36 56 L14 40 L41 40 Z"/>
        <circle cx="22" cy="18" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="80" cy="16" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="88" cy="70" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // DEPTHS SANCTUARY — the sunken gate, still lit from within.
  booster_depths_sanctuary: {
    label: "From the Depths Sanctuary Pack",
    color: "#00aaff", color2: "#9fe0ff", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M14 24 Q26 16 38 24 Q50 32 62 24 Q74 16 86 24"/>
        <path d="M30 84 L30 56 Q30 38 50 38 Q70 38 70 56 L70 84"/>
        <line x1="24" y1="84" x2="76" y2="84"/>
        <circle cx="50" cy="60" r="4" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  // HYBRID CHAOS — one circle, two natures, the zigzag between.
  booster_hybrid_chaos: {
    label: "From the Hybrid Chaos Pack",
    color: "#ec4899", color2: "#f9a8d4", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="50" r="32"/>
        <path d="M50 18 L40 38 L58 48 L42 62 L54 72 L50 82"/>
        <circle cx="34" cy="42" r="3.5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="66" cy="60" r="3.5"/>
      </g>`,
  },
  // AFFINITY ASCENSION — two rings bound, rising together.
  booster_affinity_ascension: {
    label: "From the Affinity Ascension Pack",
    color: "#10b981", color2: "#6ee7b7", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="40" cy="58" r="16"/>
        <circle cx="60" cy="58" r="16"/>
        <path d="M42 30 L50 18 L58 30"/>
        <line x1="50" y1="20" x2="50" y2="36"/>
      </g>`,
  },
  // PROPHECY ORACLE — the moon that cradles the seeing eye.
  booster_prophecy_oracle: {
    label: "From the Prophecy Oracle Pack",
    color: "#a78bfa", color2: "#ddd6fe", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M36 14 A38 38 0 1 0 36 86 A30 30 0 1 1 36 14 Z"/>
        <path d="M42 50 Q56 40 70 50 Q56 60 42 50 Z"/>
        <circle cx="56" cy="50" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="80" cy="24" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="86" cy="60" r="2" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  // ── CHAOS ───────────────────────────────────────────────────────────
  // RANDOMIZER SUPREME — the wild die: no pips, only the spiral.
  booster_randomizer_supreme: {
    label: "From the Randomizer Supreme Pack",
    color: "#f472b6", color2: "#fbcfe8", radiance: 2, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <rect x="24" y="24" width="52" height="52" rx="9" transform="rotate(-8 50 50)"/>
        <path d="M50 34 A15 15 0 1 1 36 52 A9 9 0 1 0 45 44" stroke-width="4"/>
        <circle cx="52" cy="49" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  // ── LIMITED — the Featured / Limited Edition case. Once the run sells
  // out, no more will EVER exist — so this mark flares full radiance.
  // A wax seal with ribbon tails: the collector's proof.
  limited: {
    label: "Limited Edition",
    color: "#ff4d6d", color2: "#ffd0da", radiance: 3, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M38 58 L28 88 L40 80 L46 90"/>
        <path d="M62 58 L72 88 L60 80 L54 90"/>
        <circle cx="50" cy="40" r="26"/>
        <circle cx="50" cy="40" r="17" stroke-width="3" stroke-dasharray="4 5"/>
        <path d="M44 30 L50 50 L56 30" stroke-width="4"/>
      </g>`,
  },

  // ── EMPORIUM — equipment curios from the shop's oddities shelf. A
  // clean top-hat stamp with a wink of sparkle. Restrained; it's a
  // storefront mark, not a trophy.
  emporium: {
    label: "From the Unstable Emporium",
    color: "#c8963c", color2: "#eddbb0", radiance: 1, fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M34 62 L34 30 Q34 22 42 22 L58 22 Q66 22 66 30 L66 62"/>
        <path d="M18 66 Q50 78 82 66"/>
        <line x1="34" y1="50" x2="66" y2="50" stroke-width="4"/>
        <path d="M78 26 L82 34 L90 36 L82 38 L78 46 L74 38 L66 36 L74 34 Z" stroke-width="3"/>
      </g>`,
  },

  // ══════════════════════════════════════════════════════════════
  // THE SEAL BROKEN — the two Story Mode marks
  // ══════════════════════════════════════════════════════════════
  // caP'n, 2026-08-17: "completing story mode yields a fable version
  // of 1 of each card but of course as is law, its a new source so
  // it requires a new sigil and that will be what warrants the chase
  // here. then doing every single easter egg will yield the super
  // cool version of the sigil, think the same sigil but mythical
  // version of itself."
  //
  // ONE GLYPH, TWO STATES — and the decree to keep straight is
  // "the SAME sigil, mythical version of itself." So this is
  // deliberately NOT two marks. STORY_MARK below is authored once
  // and both entries draw it; the mythic one is the identical seal
  // with the four points of a crown struck around it and its ring
  // closed in light. A player who has one recognises the other
  // instantly, which is the entire point of a chase.
  //
  // ⚠ Note this is the one place a shared recipe is CORRECT. The
  // Foil Law forbids two holo tiers sharing a motion signature —
  // but that law is about foils, and a sigil is provenance, not a
  // finish. Two provenances that are the same achievement at two
  // depths SHOULD read as one family. Do not "fix" this into two
  // unrelated glyphs.
  //
  // The mark itself: the Seal, rent. A ring broken by a jagged tear,
  // with the two Esclings standing either side of the crack —
  // because the story is finding your children, and finding them is
  // what walks you into the Seal.
  story_fable: {
    label: "Won from the Unstable World",
    color: "#cfe6c6",
    color2: "#f2fff0",
    radiance: 2,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 16 A34 34 0 1 1 49.9 16" stroke-dasharray="20 9"/>
        <path d="M46 14 L57 40 L44 52 L55 74 L47 88"/>
        <circle cx="30" cy="56" r="5"/>
        <circle cx="72" cy="46" r="5"/>
      </g>`,
  },

  // Every easter egg. The same seal, crowned.
  story_mythic: {
    label: "Won whole from the Unstable World",
    color: "#ff8fb4",
    color2: "#ffe7f0",
    radiance: 3,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="50" r="34"/>
        <path d="M46 14 L57 40 L44 52 L55 74 L47 88"/>
        <circle cx="30" cy="56" r="5" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="72" cy="46" r="5" fill="CURRENTCOLOR" stroke="none"/>
        <path d="M50 2 L54 12 L50 10 L46 12 Z" fill="CURRENTCOLOR" stroke-width="2"/>
        <path d="M50 98 L54 88 L50 90 L46 88 Z" fill="CURRENTCOLOR" stroke-width="2"/>
        <path d="M2 50 L12 46 L10 50 L12 54 Z" fill="CURRENTCOLOR" stroke-width="2"/>
        <path d="M98 50 L88 46 L90 50 L88 54 Z" fill="CURRENTCOLOR" stroke-width="2"/>
      </g>`,
  },

  // ══════════════════════════════════════════════════════════════
  // MINERIUM ONE — THE GRIDDLE SEAL, and the same seal seared
  // ══════════════════════════════════════════════════════════════
  // caP'n, 2026-08-22: *"just like the Gauntlet and everything else we
  // make that is it's own entity... of course, as is standard, its
  // very own unique sigil and the holo version as is standard with
  // these modes will be exactly the same but the sigil itself will
  // bear the holo effects of the Mythical form of its card
  // counterpart. Holos from their locations should feel unique to
  // them, Gauntlet sigil should maybe be a Gauntlet, the very first
  // Minerium belongs to Sir Hash Vrown... mini hash browns going white
  // gold to brown without being intrusive."*
  //
  // ── WHY THESE SUPERSEDE story_fable / story_mythic ────────────
  // Those two were minted when "the story" was one thing. It is not:
  // a Minerium is its own entity, the way the Gauntlet is, and
  // Minerium Two will want its own mark rather than a second helping
  // of a generic Seal. So the marks are now per-MINERIUM. The old
  // pair stays defined immediately above — collections already wear
  // it, and a provenance mark may never be rewritten under a player.
  //
  // ── THE MARK ──────────────────────────────────────────────────
  // Sir H'osh Vrown's own, and the joke is load-bearing. A GRIDDLE:
  // the round plate, one diagonal sear laid across it, six short
  // shreds hatched along the sear — the mini hash browns, drawn small
  // enough to read as texture at 28px rather than as cartoon potato —
  // and the Crimson Jewel, his seat, faceted at the centre where a
  // rank pin would sit. Nothing else in the table uses a hatched
  // diagonal or a centred facet, so it is his and only his.
  //
  // ⚠ ONE GLYPH, TWO STATES, exactly as caP'n decreed for a mode's
  // pair: MINERIUM1_GRIDDLE is authored once and both entries draw
  // it. The Mythic one is the identical seal with its rim closed in
  // light and its shreds filled — and it carries `finish: "griddle"`,
  // which is the part that matters. See THE FINISH below.
  minerium1_fable: {
    label: "Won from the Minerium of Sir H'osh Vrown",
    color: "#e6c98a",
    color2: "#fdf6e3",
    radiance: 2,
    fill: false,
    glyph: MINERIUM1_GRIDDLE("#e6c98a", false),
  },

  // Every easter egg in the Minerium. The same griddle, hot.
  minerium1_mythic: {
    label: "Won whole from the Minerium of Sir H'osh Vrown",
    color: "#c17a2e",
    color2: "#fdf6e3",
    radiance: 3,
    fill: false,
    // ⭐ THE FINISH. caP'n: the holo sigil "will bear the holo effects
    // of the Mythical form of its card counterpart." The Mythical form
    // of a Minerium One card is `holo_vrown` — "The Griddle" — so this
    // mark wears that foil's own sear rather than a sigil-shaped
    // imitation of it: the same white-gold → amber → umber ramp, the
    // same press-and-widen mid-pass, the same cooling glow after.
    // Read it as the tell: a mark with a sear crossing it is a Mythic
    // card, from across a collection grid, without reading a word.
    finish: "griddle",
    griddleColorA: "#fdf6e3",   // white-gold — the sear's leading edge
    griddleColorB: "#c17a2e",   // amber — the sear's core
    griddleColorC: "#3a2210",   // crisp umber-brown — the trailing crust
    griddleMs: 6.2,             // one full pass, matching HoloFoil's hf-griddle
    glyph: MINERIUM1_GRIDDLE("#c17a2e", true),
  },

  // ══════════════════════════════════════════════════════════════
  // THE GAUNTLET — the mode's own pair
  // ══════════════════════════════════════════════════════════════
  // Minted 2026-08-22 alongside `holo_gauntlet`, on the same law the
  // Minerium pair established: an ACTIVITY IS AN ENTITY, and an entity
  // gets its own mark, its own foil, and a Mythic mark that wears that
  // foil rather than a generic shine.
  //
  // ⚠ The mode itself is being redesigned and caP'n said not to touch
  // it. This touches nothing in the mode — a provenance mark is stamped
  // on a CARD, not on a game screen, so the pair can exist and wait.
  // When the redesign lands, the ids are already here.
  gauntlet_fable: {
    label: "Won in the Gauntlet",
    color: "#aeb8c4",
    color2: "#e9f1f8",
    radiance: 2,
    fill: false,
    glyph: THE_GAUNTLET("#aeb8c4", false),
  },

  // Every rung of it. The same fist, closed.
  gauntlet_mythic: {
    label: "Won whole in the Gauntlet",
    color: "#b9c2cc",
    color2: "#ffffff",
    radiance: 3,
    fill: false,
    // ⭐ THE FINISH, second use. `holo_gauntlet` — "The Grip" — four
    // plates close on the card and clench. The mark wears the same
    // gesture: its plates arrive, and a flash goes off at the knuckle
    // on the same beat the foil's does.
    finish: "grip",
    gripEdge:  "#f2f7fb",
    gripEmber: "#ff6a2a",
    gripMs: 4.4,
    glyph: THE_GAUNTLET("#b9c2cc", true),
  },

  // ══════════════════════════════════════════════════════════════
  // KING'S GRACE — three provenances for a companion's worn foil
  // (2026-09-16). caP'n: "that companion has its own unique mark, that
  // you got it from the booster shop, even if the official version is
  // from the story mode ... there's still a more rare version in the
  // story mode you need to get with a new sigil and new mark."
  // All three carry the CROWN (a Grace is a King's Grace); what sits
  // under the crown says where it came from. Shop marks stay quiet
  // (radiance 1); the story mark is the loud one.
  // ══════════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════════
  // VENUS' PET SHOP — a companion you BOUGHT (2026-09-16). caP'n: "the
  // story mode earnable companion, or the one you bought from the local
  // Venus' pet store." A pet tag with a paw on it: quiet (radiance 0),
  // because a bought copy is the common one; the story copy keeps the
  // loud `earned` mark.
  // ══════════════════════════════════════════════════════════════
  companion_shop: {
    label: "From Venus' Pet Shop",
    color: "#f2a6c8",
    color2: "#ffe1ee",
    radiance: 0,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <circle cx="50" cy="16" r="6"/>
        <path d="M50 22 L50 28"/>
        <path d="M50 28 C74 28 84 44 84 58 C84 76 68 88 50 88 C32 88 16 76 16 58 C16 44 26 28 50 28 Z"/>
        <ellipse cx="50" cy="66" rx="10" ry="8" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="36" cy="52" r="4.6" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="46" cy="46" r="4.6" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="56" cy="46" r="4.6" fill="CURRENTCOLOR" stroke="none"/>
        <circle cx="65" cy="52" r="4.6" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },

  grace_vault: {
    label: "Graced at the Foil Vault",
    color: "#f0cc60",
    color2: "#fff1c2",
    radiance: 1,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M20 40 L30 20 L40 34 L50 14 L60 34 L70 20 L80 40 Z"/>
        <path d="M22 48 L78 48"/>
        <circle cx="50" cy="64" r="8"/>
        <path d="M50 72 L45 86 L55 86 Z" fill="CURRENTCOLOR" stroke="none"/>
      </g>`,
  },
  grace_story: {
    label: "Graced in the Unstable World",
    color: "#ffd76a",
    color2: "#fff6d8",
    radiance: 3,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M50 16 A34 34 0 1 1 49.9 16" stroke-dasharray="22 8"/>
        <path d="M30 58 L36 40 L44 50 L50 34 L56 50 L64 40 L70 58 Z" fill="CURRENTCOLOR" fill-opacity="0.35"/>
        <path d="M32 66 L68 66"/>
        <path d="M50 2 L50 10 M50 90 L50 98 M2 50 L10 50 M90 50 L98 50" stroke-width="3.4"/>
        <path d="M18 18 L23 23 M82 18 L77 23 M18 82 L23 77 M82 82 L77 77" stroke-width="3"/>
      </g>`,
  },
  grace_annihilation: {
    label: "Graced in the Old Fold",
    color: "#e8a33d",
    color2: "#ffe6a8",
    radiance: 2,
    fill: false,
    glyph: `
      <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <path d="M26 34 L33 16 L42 28 L50 12 L58 28 L67 16 L74 34 Z" fill="CURRENTCOLOR" fill-opacity="0.2"/>
        <path d="M30 50 L50 62 L70 50"/>
        <path d="M30 64 L50 76 L70 64" opacity="0.8"/>
        <path d="M30 78 L50 90 L70 78" opacity="0.6"/>
      </g>`,
  },
};

// Unknown/legacy source → a neutral booster stamp so a card never renders
// a blank mark. A card with NO recorded source at all is "unknown origin."
export const SOURCE_FALLBACK = {
  label: "Origin Unknown",
  color: "#9a8a6a",
  color2: "#c8b890",
  radiance: 0,
  fill: false,
  glyph: `
    <g stroke="CURRENTCOLOR" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none">
      <circle cx="50" cy="50" r="32"/>
      <path d="M40 42 Q40 30 50 30 Q62 30 62 42 Q62 52 50 54 L50 60"/>
      <circle cx="50" cy="70" r="2.5" fill="CURRENTCOLOR" stroke="none"/>
    </g>`,
};

export const getSourceSigil = (source) => SOURCE_SIGILS[source] || SOURCE_FALLBACK;
export const getSourceLabel = (source) => getSourceSigil(source).label;

// Convenience for callers that want to list every defined source (pickers,
// admin tools). Order is insertion order of SOURCE_SIGILS.
export const SOURCE_ORDER = Object.keys(SOURCE_SIGILS);

// ── Keyframes — lifecycle-proof <head> injection, same proven pattern as
// HoloFoil (the module-boolean React-<style> guard had an unmount bug that
// pulled the keyframes out of the DOM; injecting straight into <head> once
// keyed off the node existing is immune to which component mounts). Class
// names namespaced `ss-` so they can never collide with `hf-*` or anything.
const SS_KEYFRAMES = `
@keyframes ss-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes ss-pulse {
  0%,100% { opacity: 0.45; transform: scale(1); }
  50%     { opacity: 0.9;  transform: scale(1.08); }
}
/* ── THE GRIP FINISH (gauntlet_mythic) ───────────────────────────
   The same beat as HoloFoil's hf-clench and for the same reason the
   Griddle finish borrows hf-griddle's law: a mark whose effect moved
   differently from the foil on the card it marks reads as two
   unrelated things. One short flash at the knuckle, per cycle. */
@keyframes ss-clench {
  0%,40%   { opacity: 0; transform: scale(0.82); }
  47%      { opacity: 1; transform: scale(1.06); }
  56%      { opacity: 0.4; transform: scale(1); }
  70%,100% { opacity: 0; transform: scale(0.88); }
}
/* ── THE GRIDDLE FINISH (minerium1_mythic) ────────────────────────
   Deliberately the SAME motion law as HoloFoil's hf-griddle, not a
   lookalike: fade in, cross to centre, PRESS — widening and dwelling
   through 42%-58% — then continue off the far edge. A mark whose sear
   moved differently from the foil on the card it marks would read as
   two unrelated effects, which is the whole thing the one-aura law
   exists to stop. Scaled down here: the sigil is 28-44px, so the band
   is narrower and the blur smaller, and it never brightens past the
   glyph it is crossing. */
@keyframes ss-griddle {
  0%   { transform: translateX(-150%) skewX(-13deg) scaleX(1);    opacity: 0; }
  14%  { opacity: 1; }
  42%  { transform: translateX(-10%)  skewX(-13deg) scaleX(1.08); opacity: 1; }
  58%  { transform: translateX(10%)   skewX(-13deg) scaleX(1.14); opacity: 1; }
  86%  { opacity: 1; }
  100% { transform: translateX(150%)  skewX(-13deg) scaleX(1);    opacity: 0; }
}
@keyframes ss-griddle-cool {
  0%,100% { opacity: 0.18; }
  50%     { opacity: 0.42; }
}
@keyframes ss-glint {
  0%   { transform: translateX(-140%) skewX(-18deg); opacity: 0; }
  20%  { opacity: 0.9; }
  55%  { transform: translateX(140%)  skewX(-18deg); opacity: 0; }
  100% { transform: translateX(140%)  skewX(-18deg); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .ss-halo, .ss-glow, .ss-glint, .ss-griddle, .ss-griddle-cool, .ss-clench { animation: none !important; }
}
`;
const SS_STYLE_ID = "ss-keyframes";
function ensureSigilKeyframes() {
  if (typeof document === "undefined") return;       // SSR-safe
  if (document.getElementById(SS_STYLE_ID)) return;  // already in <head>
  const el = document.createElement("style");
  el.id = SS_STYLE_ID;
  el.textContent = SS_KEYFRAMES;
  document.head.appendChild(el);
}
ensureSigilKeyframes();

// ── SourceSigil — the mark. Self-contained square box; no special parent
// needed. Layers, back to front: rotating halo → breathing glow → glyph →
// sweeping glint.
//
//   source     source id (e.g. "booster_shadowfox"). Unknown → fallback.
//   size       px square (default 40).
//   intensity  0..1 multiplier on the animated layers' opacity (default 1).
//   className  extra class on the root.
export default function SourceSigil({
  source = "booster",
  size = 40,
  intensity = 1,
  className = "",
  style = {},
}) {
  ensureSigilKeyframes();
  const s = getSourceSigil(source);
  const r = s.radiance || 0;
  const I = Math.max(0, Math.min(1, intensity));

  const showHalo  = r >= 2;
  const showGlow  = r >= 1;
  const showGlint = r >= 3;

  const glyphSvg = s.glyph.replace(/CURRENTCOLOR/g, s.color);

  return (
    <div
      className={`ss-root ${className}`}
      aria-hidden="true"
      title={s.label}
      style={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        pointerEvents: "none",
        overflow: "hidden",
        ...style,
      }}
    >
      {/* Rotating halo behind the glyph (radiance ≥ 2) */}
      {showHalo && (
        <div
          className="ss-halo"
          style={{
            position: "absolute",
            inset: "-15%",
            borderRadius: "50%",
            background: `conic-gradient(from 0deg, transparent 0deg, ${s.color}55 70deg, transparent 150deg, ${s.color2 || s.color}55 230deg, transparent 320deg)`,
            animation: "ss-spin 6s linear infinite",
            opacity: 0.7 * I,
            filter: "blur(2px)",
          }}
        />
      )}

      {/* Breathing radial glow (radiance ≥ 1) */}
      {showGlow && (
        <div
          className="ss-glow"
          style={{
            position: "absolute",
            inset: "5%",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${s.color}66 0%, ${s.color}22 45%, transparent 70%)`,
            animation: "ss-pulse 2.8s ease-in-out infinite",
            opacity: I,
          }}
        />
      )}

      {/* The glyph */}
      <svg
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
        style={{
          position: "absolute",
          inset: 0,
          filter: r >= 2
            ? `drop-shadow(0 0 4px ${s.color}cc) drop-shadow(0 0 9px ${s.color}66)`
            : r >= 1
            ? `drop-shadow(0 0 3px ${s.color}aa)`
            : `drop-shadow(0 0 1px ${s.color}88)`,
        }}
        dangerouslySetInnerHTML={{ __html: glyphSvg }}
      />

      {/* Sweeping specular glint across the glyph (radiance 3 only) */}
      {showGlint && (
        <div
          className="ss-glint"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.55) 50%, transparent 60%)",
            mixBlendMode: "screen",
            animation: "ss-glint 3.6s ease-in-out infinite",
            opacity: I,
          }}
        />
      )}

      {/* ── THE FINISH — a foil worn by a MARK, not by a card ───────
          caP'n's law for a mode's pair: the holo sigil is the same
          sigil "but bear[ing] the holo effects of the Mythical form of
          its card counterpart." So this is not a generic "shiny"
          option — it is keyed to a named foil tier, and today exactly
          one recipe declares one. A future Minerium adds its own
          `finish` beside this branch and inherits nothing.

          Two layers, both restrained on purpose. The SEAR crosses in
          overlay so it colours the glyph's own strokes rather than
          painting a bar over them — white-gold in front, amber
          through the middle, umber-brown trailing. The COOLING GLOW
          settles low and warm afterwards, the same way holo_vrown's
          corona does on a card. Nothing here brightens past the mark;
          "without being intrusive" is the brief. */}
      {s.finish === "griddle" && (
        <>
          <div
            className="ss-griddle"
            style={{
              position: "absolute",
              inset: "-20% -45%",
              background: `linear-gradient(100deg, transparent 0%, ${s.griddleColorA || "#fdf6e3"}ee 34%, ${s.griddleColorB || "#c17a2e"}dd 52%, ${s.griddleColorC || "#3a2210"}bb 68%, transparent 100%)`,
              animation: `ss-griddle ${s.griddleMs || 6.2}s ease-in-out infinite`,
              mixBlendMode: "overlay",
              opacity: 0.72 * I,
              filter: "blur(1.2px)",
            }}
          />
          <div
            className="ss-griddle-cool"
            style={{
              position: "absolute",
              inset: "18% 8% -12% 8%",
              borderRadius: "50%",
              background: `radial-gradient(ellipse at 50% 80%, ${s.griddleColorC || "#3a2210"}88 0%, transparent 68%)`,
              animation: `ss-griddle-cool ${(s.griddleMs || 6.2) * 0.9}s ease-in-out infinite`,
              mixBlendMode: "multiply",
              opacity: I,
            }}
          />
        </>
      )}

      {/* ── THE GRIP FINISH — `holo_gauntlet` worn by a mark.
          One layer, not two: the plates are already IN the glyph (they
          are drawn arrived on the hot state), so all the finish has to
          supply is the moment they land. A white knuckle-flash with an
          ember under it, on the foil's own beat. */}
      {s.finish === "grip" && (
        <div
          className="ss-clench"
          style={{
            position: "absolute",
            inset: "24%",
            borderRadius: "44%",
            background: `radial-gradient(circle, ${s.gripEdge || "#f2f7fb"} 0%, ${s.gripEmber || "#ff6a2a"}bb 38%, transparent 74%)`,
            animation: `ss-clench ${s.gripMs || 4.4}s ease-out infinite`,
            mixBlendMode: "screen",
            opacity: 0.85 * I,
            filter: "blur(1.6px)",
          }}
        />
      )}
    </div>
  );
}

// ── THE ONE-ORIGIN LAW ────────────────────────────────────────────────
// Every card and companion shows exactly ONE mark under exactly ONE word:
// ORIGIN. No pedestals, no "your copies", no mark clusters. Resolution:
//   1. If you own copies, your copies' top provenance IS the origin —
//      your H-01 came from your starter grimoire, so that's its truth.
//   2. Otherwise the canonical primary (progression.getCardOrigins is
//      ordered starter → packs → vault, so [0] is the strongest claim).
// Opponent cards resolve canonically. No card is ever unmarked.
export function primaryOrigin(rec, origins = []) {
  const counts = {};
  if (rec?.sources) for (const [s, n] of Object.entries(rec.sources)) counts[s] = (counts[s] || 0) + (n || 0);
  if (rec?.sourceTiers) for (const m of Object.values(rec.sourceTiers)) for (const [s, n] of Object.entries(m || {})) counts[s] = (counts[s] || 0) + (n || 0);
  const top = Object.entries(counts).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1])[0];
  if (top) return top[0];
  return origins[0] || null;
}

// The single identifier strip: ORIGIN · mark · its name. Pure view.
export function OriginMark({ source, size = 30, showText = true, style = {} }) {
  if (!source) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, ...style }}>
      <span style={{ fontSize: 8, fontWeight: 900, letterSpacing: 2.5, color: "#8a6520", textTransform: "uppercase" }}>
        Origin
      </span>
      <SourceSigil source={source} size={size} />
      {showText && (
        <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1.2, color: "#c8963c", textTransform: "uppercase", maxWidth: 160, lineHeight: 1.35, textAlign: "left" }}>
          {getSourceLabel(source)}
        </span>
      )}
    </div>
  );
}
